import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { appConstants } from '../../../shared/constants/constants';
import { FormValidators } from '../../../shared/interfaces/form-validators';
import { DocumentSpecsObject } from '../../../shared/interfaces/document-specs-object';

import {
  APIService,
  CreateEnvasadoInput,
  CreateEnvasadoMutation,
  CreateNotificationInput,
  Document,
  Envasado,
  UpdateEnvasadoInput,
} from '../../../app-sync.service';
import { DocumentsService } from '../../documents/documents.service';
import { FeedbacksService } from '../../../shared/feedbacks/feedbacks.service';
import { MasterService } from '../../master/master.service';
import { SettingsService } from '../../settings/settings.service';
import { UsersService } from '../../users/users.service';
import { ValidatorsService } from '../../../shared/services/validators.service';
import { EnvasadosService } from '../envasados.service';
import { FormsService } from '../../../shared/services/forms.service';
import { DocumentFromForms } from '../../../shared/interfaces/document-from-forms';
import { EditionsService } from '../../../shared/services/editions.service';
import { ArchivesService } from '../../../shared/services/archives.service';
import { CommonsService } from '../../../shared/services/commons.service';

@Component({
  selector: 'app-envasado-edit',
  templateUrl: './envasado-edit.component.html',
  styleUrls: ['./envasado-edit.component.css'],
})
export class EnvasadoEditComponent implements OnInit, OnDestroy {
  entityName: string = 'Envasado Rígido';
  selectedEnvasado: Envasado | undefined = undefined;
  envasadoIdentification: string = '';
  equipType: string = '';
  equipTypeToDisplay: string = '';
  currentYear: number = new Date().getFullYear();
  editMode: boolean = true;
  archiveMode: boolean = false;
  isAdmin: boolean = false;
  isCarrier: boolean = false;
  isGettingEnvasado: boolean = false;
  master: any = {};
  masterDisplayMap: any = {};
  documentsSpecs: DocumentSpecsObject = {};
  selectedCenter: string = '';
  envasadoForm: FormGroup;
  envasadoId: string = '';
  envasadoIsOld: boolean = false;
  isDev: boolean = environment.env === 'dev' || environment.env === 'sandbox';
  business: string;
  editionId: string = '';

  get documentsControl() {
    return (<FormArray>this.envasadoForm.get('documents')).controls;
  }

  get disableSubmit() {
    return (
      this.envasadoForm.invalid ||
      this.envasadoForm.pristine ||
      this.envasadoForm.untouched
    );
  }

  constructor(
    private api: APIService,
    private documentsService: DocumentsService,
    private feedbacksService: FeedbacksService,
    private masterService: MasterService,
    private route: ActivatedRoute,
    private router: Router,
    private settingsService: SettingsService,
    private usersService: UsersService,
    private validatorsService: ValidatorsService,
    private envasadosService: EnvasadosService,
    private formsService: FormsService,
    private editionsService: EditionsService,
    private archivesService: ArchivesService,
    private commonsService: CommonsService
  ) {
    this.envasadoForm = new FormGroup({
      carrier: new FormControl(null),
      licensePlate: new FormControl(null),
      brand: new FormControl(null),
      model: new FormControl(null),
      year: new FormControl(null),
      color: new FormControl(null),
      type: new FormControl(null),
      capacity: new FormControl(null),
      center: new FormControl(null),
      gpsImei: new FormControl(null),
      cameraImei: new FormControl(null),
      forMining: new FormControl(null),
      engineType: new FormControl(null),
      hasRanchAccreditation: new FormControl(null),
      isSpot: new FormControl(null),
      bodyType: new FormControl(null),
      maximumPalletCapacity: new FormControl(null),
      height: new FormControl(null),
      width: new FormControl(null),
      length: new FormControl(null),
      onlyMandatory: new FormControl(false),
      documents: this.formsService.documentsFormArrayInitialization(),
    });
    this.business = this.usersService.business.value.toUpperCase();
  }

  ngOnInit() {
    this.isGettingEnvasado = true;
    this.isAdmin = this.usersService.isAdmin;
    this.isCarrier = this.usersService.isCarrier;
    this.master = this.masterService.getMaster();
    this.masterDisplayMap = this.masterService.getMasterDisplayMap();
    this.archiveMode = this.archivesService.getArchiving();
    this.selectedEnvasado = this.envasadosService.getSelectedEnvasado();
    this.envasadoIdentification = this.commonsService.getIdentification(
      this.selectedEnvasado.sapId!,
      this.selectedEnvasado.licensePlate
    );

    this.equipType = this.master['ENVASADOS#TYPES'][0].valueId;
    this.equipTypeToDisplay =
      this.masterDisplayMap['ENVASADOS#TYPES'][this.equipType];

    this.route.params.subscribe(async (params: Params): Promise<void> => {
      // Si la ruta contiene un ID de envasado rígido
      // estamos en modo edición
      this.editMode = params['id'] != null;
      this.envasadoId = params['id'];

      this.initEnvasadoForm();
      if (!this.editMode) {
        if (this.settingsService.getSelectedModel() !== 'ENVASADO') {
          console.log('Loading document specifications');
          this.settingsService.setSelectedModel('ENVASADO');
          await this.settingsService.setDocumentsSpecs();
        } else {
          console.log('Reusing document specifications');
        }
        this.documentsSpecs = this.settingsService.getDocumentsSpecs();
      } else {
        this.editionId = `${this.business}#ENVASADO#${this.envasadoId}`;
        await this.editionsService.createOrUpdateEdition(this.editionId);
      }

      // Ordenamos alfabéticamente el formulario de editar las propiedades
      this.documentsControl.sort((a, b) => {
        const documentNameA = a.value.documentName.toLowerCase();
        const documentNameB = b.value.documentName.toLowerCase();
        return documentNameA.localeCompare(documentNameB);
      });

      this.isGettingEnvasado = false;
    });
  }

  /* ------------------------------------------- */
  /* - Métodos para la creación del Formulario - */
  /* ------------------------------------------- */
  /**
   * Inicializa el formulario para crear o
   * editar un envasado.
   * @private
   */
  private initEnvasadoForm(): void {
    // Inicialización del Formulario
    // Por default, los campos estarán vacíos.
    // Si el usuario es Transportista el campo carrier campo estará seleccionado.
    const editingOrArchiving: boolean = this.editMode || this.archiveMode;
    let initialEnvasado: Envasado = {
      ...appConstants.envasado.initialization,
      carrier: appConstants.carrier.initialization,
    };
    let envasadoDocuments: FormArray =
      this.formsService.documentsFormArrayInitialization();

    // Si es transportista la única opción de carrier es él mismo.
    initialEnvasado.carrier.carrierId = this.isCarrier
      ? this.usersService.getActiveUser().company
      : '';
    // El tipo de equipo es un valor fijo.
    initialEnvasado.type = this.equipType;

    if (editingOrArchiving) {
      // En modo de Edición: cargamos los valores de los campos
      // según el envasado rígido escogido.
      // En modo Archivar: cargamos los valores de los campos según
      // el envasado archivado (que sigue siendo el seleccionado).
      initialEnvasado = this.selectedEnvasado!;
      const selectedEnvasadoDocuments: Document[] =
        this.documentsService.getDocuments();

      const envasadoAge: number = this.currentYear - initialEnvasado.year;
      let envasadoHasRecertificationOfManufacturing: boolean = false;

      let documentMasterValueIdsList: string[] = [];
      envasadoDocuments.removeAt(0);
      for (let document of selectedEnvasadoDocuments) {
        envasadoDocuments.push(
          this.formsService.createDocumentControlFromDocument(
            document,
            !this.archiveMode
          )
        );
        documentMasterValueIdsList.push(document.masterValueId);
        // Verificamos si el Envasado Rígido tiene el Re-certificado de Fabricación
        if (
          document.masterValueId ===
          `${this.business}_${appConstants.document.codes.recertification_of_manufacturing}`
        ) {
          envasadoHasRecertificationOfManufacturing = true;
        }
      }

      // Si la edad del envasado rígido es >= appConstants.envasado.ageToBeConsideredOld
      // y el envasado rígido NO tiene asignado un documento Re-certificado de Fabricación
      // se agrega al formulario.
      if (
        envasadoAge >= appConstants.envasado.ageToBeConsideredOld &&
        !envasadoHasRecertificationOfManufacturing
      ) {
        // Agregamos el Re-certificado de Fabricación
        const index = this.master['ENVASADOS#DOCUMENT_TYPES'].findIndex(
          (document: any) => {
            return (
              document.valueId ===
              `${this.business}_${appConstants.document.codes.recertification_of_manufacturing}`
            );
          }
        );
        const document = this.master['ENVASADOS#DOCUMENT_TYPES'][index];

        envasadoDocuments.push(
          this.formsService.createDocumentControlFromMaster(document)
        );
        this.envasadoIsOld = true;
      }

      // Agregamos al formulario los documentos que existen en el maestro y que no
      // han sido cargados hasta el momento.
      envasadoDocuments = this.setAdditionalDocumentsFormGroup(
        initialEnvasado.center,
        envasadoDocuments,
        documentMasterValueIdsList
      );
    } else {
      // En modo Creación: Creamos un FormControl por cada documento
      // requerido según el Maestro
      envasadoDocuments = this.setInitialDocumentsFormGroup('');
    }

    // Inicialización del formulario
    this.envasadoForm = new FormGroup({
      carrier: new FormControl(
        { value: initialEnvasado.carrier.carrierId, disabled: this.editMode },
        Validators.required
      ),
      licensePlate: new FormControl(
        {
          value: initialEnvasado.licensePlate,
          disabled: this.disableLicensePlate(),
        },
        [
          Validators.required,
          this.validatorsService.licensePlatePatternValidator(this.business),
        ],
        [this.validatorsService.licensePlateInUseValidator()]
      ),
      brand: new FormControl(
        { value: initialEnvasado.brand, disabled: this.editMode },
        Validators.required
      ),
      model: new FormControl(
        { value: initialEnvasado.model, disabled: this.editMode },
        Validators.required
      ),
      year: new FormControl(
        {
          value: this.checkNumberValue(initialEnvasado.year),
          disabled: this.editMode,
        },
        [Validators.required, Validators.pattern(appConstants.regex.year)]
      ),
      color: new FormControl(
        { value: initialEnvasado.color, disabled: this.editMode },
        Validators.required
      ),
      type: new FormControl(
        { value: initialEnvasado.type, disabled: this.editMode },
        Validators.required
      ),
      capacity: new FormControl(
        { value: initialEnvasado.capacity, disabled: this.editMode },
        Validators.required
      ),
      center: new FormControl(
        { value: initialEnvasado.center, disabled: this.editMode },
        Validators.required
      ),
      gpsImei: new FormControl({
        value: initialEnvasado.gpsImei,
        disabled: this.editMode,
      }),
      cameraImei: new FormControl({
        value: initialEnvasado.cameraImei,
        disabled: this.editMode,
      }),
      forMining: new FormControl(
        {
          value: this.formsService.radioButtonsTypeParsing(
            editingOrArchiving,
            initialEnvasado.forMining
          ),
          disabled: this.editMode,
        },
        Validators.required
      ),
      engineType: new FormControl(
        { value: initialEnvasado.engineType, disabled: this.editMode },
        Validators.required
      ),
      hasRanchAccreditation: new FormControl(
        {
          value: this.formsService.radioButtonsTypeParsing(
            editingOrArchiving,
            initialEnvasado.hasRanchAccreditation
          ),
          disabled: this.editMode,
        },
        Validators.required
      ),
      isSpot: new FormControl(
        {
          value: this.formsService.radioButtonsTypeParsing(
            editingOrArchiving,
            initialEnvasado.isSpot
          ),
          disabled: this.editMode,
        },
        Validators.required
      ),
      bodyType: new FormControl(
        { value: initialEnvasado.bodyType, disabled: this.editMode },
        Validators.required
      ),
      maximumPalletCapacity: new FormControl(
        {
          value:
            initialEnvasado.maximumPalletCapacity === 0
              ? null
              : initialEnvasado.maximumPalletCapacity,
          disabled: this.editMode,
        },
        Validators.required
      ),
      height: new FormControl(
        {
          value: this.checkNumberValue(initialEnvasado.height),
          disabled: this.editMode,
        },
        Validators.required
      ),
      width: new FormControl(
        {
          value: this.checkNumberValue(initialEnvasado.width),
          disabled: this.editMode,
        },
        Validators.required
      ),
      length: new FormControl(
        {
          value: this.checkNumberValue(initialEnvasado.length),
          disabled: this.editMode,
        },
        Validators.required
      ),
      onlyMandatory: new FormControl(true),
      documents: envasadoDocuments,
    });
  }

  /**
   * Verifica el valor de un atributo para retornar nulo o el valor del atributo.
   * @param {number | null | undefined} attributeValue Valor del atributo.
   * @return {number | null | undefined} El valor del atributo, si es distinto de cero, o nulo, en caso contrario.
   * @private
   */
  private checkNumberValue(
    attributeValue: number | null | undefined
  ): number | null | undefined {
    return attributeValue === 0 ? null : attributeValue;
  }

  /**
   * Define el arreglo de controles de documentos para el formulario.
   * @param {string} center Centro al que pertenece el envasado rígido.
   * @return {FormArray}
   * @private
   */
  private setInitialDocumentsFormGroup(center: string): FormArray {
    let envasadoDocuments =
      this.formsService.documentsFormArrayInitialization();

    let documentTypes = this.master['ENVASADOS#DOCUMENT_TYPES'];
    documentTypes.sort(function (a: any, b: any) {
      let x = a.valueToDisplay.toLowerCase();
      let y = b.valueToDisplay.toLowerCase();
      if (x < y) {
        return -1;
      }
      if (x > y) {
        return 1;
      }
      return 0;
    });

    envasadoDocuments.removeAt(0);
    for (let document of documentTypes) {
      // Se excluye el Re-certificado de Fabricación, ya que se agrega según el año de fabricación
      if (
        document.valueId !==
        `${this.business}_${appConstants.document.codes.recertification_of_manufacturing}`
      ) {
        const validators: FormValidators = this.getValidators(
          document.valueId,
          center
        );
        envasadoDocuments.push(
          this.formsService.createDocumentControlFromMasterWithValidators(
            document,
            validators
          )
        );
      }
    }

    return envasadoDocuments;
  }

  /**
   * Dado un conjunto de documentos, agrega los controles de documentos
   * faltantes según el maestro, para el formulario.
   * @param {string} center Centro al que pertenece el envasado.
   * @param {FormArray} envasadoDocuments Arreglo de controles de documentos.
   * @param {string[]} documentMasterValueIdsList Lista de Ids de documentos incluídos en el envasadoDocuments.
   * @return {FormArray}
   * @private
   */
  private setAdditionalDocumentsFormGroup(
    center: string,
    envasadoDocuments: FormArray,
    documentMasterValueIdsList: string[]
  ): FormArray {
    let documentTypes = this.master['ENVASADOS#DOCUMENT_TYPES'];
    documentTypes.sort(function (a: any, b: any) {
      let x = a.valueToDisplay.toLowerCase();
      let y = b.valueToDisplay.toLowerCase();
      if (x < y) {
        return -1;
      }
      if (x > y) {
        return 1;
      }
      return 0;
    });

    const business: string = this.usersService.business.value.toUpperCase();
    for (let document of documentTypes) {
      // Se excluye el Re-certificado de Fabricación, ya que se agrega según el año de fabricación
      // Se agregan solo los documentos que no se encuentren ya en el arreglo de controles.
      if (
        document.valueId !==
          `${business}_${appConstants.document.codes.recertification_of_manufacturing}` &&
        documentMasterValueIdsList.indexOf(document.valueId) === -1
      ) {
        const validators: FormValidators = this.getValidators(
          document.valueId,
          center
        );
        envasadoDocuments.push(
          this.formsService.createDocumentControlFromMasterWithValidators(
            document,
            validators
          )
        );
      }
    }

    return envasadoDocuments;
  }

  /**
   * Crea un objeto con las funciones de validación para cada uno
   * de los controles del formulario.
   * @param {string }center Centro al que pertenece el envasado.
   * @param {string} masterValueId ID del tipo de documento en el maestro.
   * @return {FormValidators}
   * @private
   */
  private getValidators(
    masterValueId: string,
    center: string = ''
  ): FormValidators {
    const isMandatory: boolean =
      this.documentsSpecs[center + '#' + masterValueId];
    return this.validatorsService.getDocumentValidatorForm(center, isMandatory);
  }

  /* ---------------------------------------- */
  /* - Métodos de validación del Formulario - */
  /* ---------------------------------------- */
  /**
   * Determina, a través del servicio de validadores,
   * si debe mostrar la ayuda de un control del formulario.
   * @param {AbstractControl} control Control del formulario.
   * @return {Boolean}
   */
  showHelper(control: AbstractControl<any, any> | null): boolean | undefined {
    return this.validatorsService.showHelper(control);
  }

  /**
   * Determina si un control del formulario es válido.
   * @param {AbstractControl} control Control del formulario.
   * @return {Boolean}
   */
  invalidControl(control: AbstractControl<any, any> | null): boolean {
    return !control?.valid;
  }

  /**
   * Consulta el mensaje de ayuda para un control del formulario.
   * @param {AbstractControl} control Control del formulario.
   * @return {string} Ayuda para el usuario.
   */
  helperMessages(control: AbstractControl<any, any> | null): string {
    return this.validatorsService.helperMessages(control);
  }

  /* --------------------------------------- */
  /* - Métodos que modifican el Formulario - */
  /* --------------------------------------- */
  /**
   * Actualiza la obligatoriedad de los documentos
   * según el centro seleccionado.
   */
  onEnvasadoCenterChange(): void {
    if (this.selectedCenter === this.envasadoForm.get('center')?.value) {
      // No cambia el formulario
      console.log('Cambio de centro no altera el formulario');
    } else {
      // Actualizamos los documentos requeridos según el centro.
      this.selectedCenter = this.envasadoForm.get('center')?.value;
      console.log('Actualizamos los documentos requeridos según el centro');
      const envasadoDocuments = this.setInitialDocumentsFormGroup(
        this.selectedCenter
      );
      this.envasadoForm.removeControl('documents');
      this.envasadoForm.setControl('documents', envasadoDocuments);
    }
  }

  /**
   * Ejecuta un flujo para agregar o remover el Re-certificado de fabricación
   * a la lista de documentos dependiendo de la edad del envasado rígido.
   */
  onEnvasadoYearChange(): void {
    // En caso de que el envasado rígido haya sido
    // fabricado hace 7 años o más, el
    // Re-certificado de Fabricación será solicitado.
    if (
      this.currentYear - this.envasadoForm.get('year')?.value >=
      appConstants.envasado.ageToBeConsideredOld
    ) {
      this.onEnvasadoIsOld();
    } else {
      if (this.envasadoIsOld) {
        // Si antes se había seleccionado un año que hacía
        // que la edad del envasado rígido fuese >= appConstants.envasado.ageToBeConsideredOld
        // la variable envasadoIsOld será verdadera
        // Removemos el control de formulario
        // asociado al Re-certificado de Fabricación
        this.onEnvasadoIsYoung();
      }
    }
  }

  /**
   * Agregar el Re-certificado de fabricación a la lista de documentos
   * y define la variable envasadoIsOld = True.
   * @private
   */
  private onEnvasadoIsOld(): void {
    if (!this.envasadoIsOld) {
      // Agregamos el Re-certificado de Fabricación
      const index = this.master['ENVASADOS#DOCUMENT_TYPES'].findIndex(
        (document: any): boolean => {
          return (
            document.valueId ===
            `${this.business}_${appConstants.document.codes.recertification_of_manufacturing}`
          );
        }
      );
      const document = this.master['ENVASADOS#DOCUMENT_TYPES'][index];

      (<FormArray>this.envasadoForm.get('documents')).push(
        this.formsService.createDocumentControlFromMaster(document)
      );
      this.envasadoIsOld = true;
    }
  }

  /**
   * Remueve el Re-certificado de fabricación a la lista de documentos
   * y define la variable envasadoIsOld = False.
   * @private
   */
  private onEnvasadoIsYoung(): void {
    let documentFormArray = <FormArray>this.envasadoForm.get('documents');
    const index: number = (<Array<any>>documentFormArray.value).findIndex(
      (document: any): boolean => {
        return (
          document.documentMasterValueId ===
          `${this.business}_${appConstants.document.codes.recertification_of_manufacturing}`
        );
      }
    );
    documentFormArray.removeAt(index);
    this.envasadoForm.setControl('documents', documentFormArray);
    this.envasadoIsOld = false;
  }

  /**
   * Asigna el archivo al control documentSourceFile
   * del documento.
   * @param {Event} event Evento input.
   * @param {number} index Índice del documento en el arreglo documents.
   */
  onChangeDocumentFile(event: Event, index: number): void {
    // Asignamos el archivo al FormControl 'documentSourceFile'
    const target = event.target as HTMLInputElement;
    console.log(' --- onChangeDocumentFile ---');
    console.log(target);
    console.log(target.files![0]);
    if (target.files!.length > 0) {
      const file: File = target.files![0];
      (<FormArray>this.envasadoForm.get('documents'))
        .at(index)
        .patchValue({ documentSourceFile: file });
    }
  }

  /**
   * Retorna el ID del Transportista al que pertenece un usuario
   * conductor o transportista.
   * @return {string} ID del transportista.
   */
  carrierValueId(): string {
    return this.usersService.getActiveUser().company;
  }

  /**
   * Retorna el nombre del Transportista al que pertenece un usuario
   * conductor o transportista.
   * @return {string} Nombre del transportista.
   */
  carrierValueToDisplay(): string {
    const company: string = this.usersService.getActiveUser().company;
    return this.masterDisplayMap['CARRIERS'][company];
  }

  /**
   * Consulta, a través del servicio de documentos, si un tipo de documento
   * requiere fechas de emisión y vencimiento.
   * @param {string} masterValueId ID del tipo de documento en el maestro.
   * @return {boolean}
   */
  isDatelessDocument(masterValueId: string): boolean {
    return this.documentsService.isDatelessDocument(masterValueId);
  }

  /**
   * Consulta, a través del servicio de documentos, si un tipo de documento
   * es obligatorio para un centro.
   * @param {string} masterValueId ID del tipo de documento en el maestro.
   * @return {boolean}
   */
  isMandatoryDocument(masterValueId: string): boolean {
    const center = this.envasadoForm.get('center')?.value;
    if (this.documentsSpecs[center + '#' + masterValueId]) {
      return this.documentsSpecs[center + '#' + masterValueId];
    } else {
      return false;
    }
  }

  /**
   * Determina si se debe deshabilitar el input
   * para la patente.
   */
  private disableLicensePlate(): boolean {
    return (
      this.editMode ||
      (this.archiveMode && !this.archivesService.hasToChangeID())
    );
  }

  /* ------------------------------- */
  /* - Métodos asociados a botones - */
  /* ------------------------------- */
  /**
   * Crea o edita un envasado rígido.
   */
  async onSubmit(): Promise<void> {
    console.log(' onSubmit @ envasado-edit.component ');
    console.log(this.envasadoForm.value);
    const newEnvasado = this.envasadoForm.value;
    const documentList: DocumentFromForms[] =
      this.envasadoForm.value.documents.slice();

    if (this.disableLicensePlate()) {
      // Si la patente fue deshabilitada se asigna acá.
      newEnvasado['licensePlate'] = this.selectedEnvasado!.licensePlate;
    }

    console.log('newEnvasado', newEnvasado);
    console.log('documentList', documentList);

    const uploadDateYYYYMMDD = new Date()
      .toISOString()
      .split('T')[0]
      .replaceAll('-', '');
    const uploadDateTimestamp = Math.floor(Date.now() / 1000).toString();

    if (this.editMode) {
      // Chequeamos si al menos un documento ha sido editado.
      // De lo contrario no se actualiza el envasado rígido.
      let envasadoEdited: boolean = false;
      documentList.forEach((document: any) => {
        envasadoEdited = envasadoEdited || Boolean(document.documentSourceFile);
        console.log(envasadoEdited);
      });
      if (!envasadoEdited) {
        this.feedbacksService.showFeedback(
          'Debe adjuntar al menos un documento para editar el envasado rígido',
          'danger'
        );
        return;
      }

      // Nota: En el modo edición los ID fueron fijados al momento de iniciar el formulario.
      this.feedbacksService.showFeedback(
        'Actualizando envasado rígido: ' + this.envasadoId,
        'info'
      );

      await this.notifyEdition(uploadDateTimestamp);

      // En modo edición, solo pueden editarse los documentos
      // Sin embargo, el envasado rígido vuelve a quedar en estado de "Por Aprobar"
      // 1. Se cambia en estatus del envasado rígido
      const updateEnvasadoInput: UpdateEnvasadoInput = {
        envasadoId: this.envasadoId,
        status: `${this.business}_${appConstants.entity.codes.toBeApproved}`,
        updatedBy: this.usersService.getActiveUser().userId,
      };

      await this.api
        .UpdateEnvasado(updateEnvasadoInput)
        .then(() => {
          // 2. Se cargan los documentos del envasado rígido, si aplica.
          this.documentsService.uploadOrCopyDocuments(
            this.usersService.business.value,
            'ENVASADO',
            this.envasadoId,
            documentList,
            uploadDateYYYYMMDD,
            uploadDateTimestamp
          );
        })
        .catch((response: any): void => {
          this.feedbacksService.showErrorFeedbacks(
            response,
            `Error al actualizar envasado rígido ${this.envasadoId}`
          );
        });
    } else {
      // Si es un flujo de archivado,
      // Se elimina primero la entidad anterior
      // para evitar conflictos si es la misma patente.
      if (this.archiveMode) {
        // 1. Eliminar entidad anterior
        // (en este punto sigue como la que está seleccionada)
        await this.envasadosService.deleteSelectedEnvasado(false);

        // 2. Liberamos el modo de archivado
        this.archivesService.setArchiving(false);
      }

      // En el modo crear los ID se toman desde el formulario.
      this.envasadoId = newEnvasado.licensePlate.toUpperCase();

      this.feedbacksService.showFeedback(
        `Creando envasado rígido: ${this.envasadoId}`,
        'info'
      );

      // En modo creación o archivo:
      // 1. Se crea un nuevo envasado rígido.
      const createEnvasadoInput: CreateEnvasadoInput = {
        envasadoId: this.envasadoId,
        brand: newEnvasado.brand,
        licensePlate: this.envasadoId, // La licencia corresponde con el ID
        color: newEnvasado.color,
        model: newEnvasado.model.toUpperCase(),
        year: newEnvasado.year,
        type: newEnvasado.type,
        capacity: newEnvasado.capacity,
        business: this.business,
        center: newEnvasado.center,
        originalCenter: newEnvasado.center,
        blockingMotives: [],
        gpsImei: newEnvasado.gpsImei,
        cameraImei: newEnvasado.cameraImei,
        forMining: newEnvasado.forMining === 'true',
        engineType: newEnvasado.engineType,
        hasRanchAccreditation: newEnvasado.hasRanchAccreditation === 'true',
        isSpot: newEnvasado.isSpot === 'true',
        bodyType: newEnvasado.bodyType,
        maximumPalletCapacity: newEnvasado.maximumPalletCapacity,
        height: newEnvasado.height,
        width: newEnvasado.width,
        length: newEnvasado.length,
        status: `${this.business}_${appConstants.entity.codes.toBeApproved}`, // Un nuevo envasado rígido debe ser aprobado
        company: newEnvasado.carrier,
        carrierEnvasadosCarrierId: newEnvasado.carrier,
        updatedBy: this.usersService.getActiveUser().userId,
        ...(this.archiveMode && {
          archiveId: this.archivesService.getArchiveIdForEntity(),
        }),
      };
      console.log('createEnvasadoInput', createEnvasadoInput);

      this.api
        .CreateEnvasado(createEnvasadoInput)
        .then((envasado: CreateEnvasadoMutation) => {
          this.feedbacksService.showFeedback(
            `Envasado Rígido ${envasado.envasadoId} creado correctamente`,
            'success'
          );

          // 2. Se cargan los documentos del envasado rígido.
          this.documentsService.uploadOrCopyDocuments(
            this.usersService.business.value,
            'ENVASADO',
            this.envasadoId,
            documentList,
            uploadDateYYYYMMDD,
            uploadDateTimestamp,
            this.archiveMode
          );
        })
        .catch((response: any): void => {
          this.feedbacksService.showErrorFeedbacks(
            response,
            `Error al crear envasado rígido ${this.envasadoId}`
          );
        });
    }

    this.router
      .navigate(['../'], { relativeTo: this.route })
      .then(() => console.log('navigate back'));
  }

  /**
   * Notifica la edición de una cisterna, si aplica.
   * @param {string} uploadDateTimestamp Timestamp de edición.
   */
  private async notifyEdition(uploadDateTimestamp: string): Promise<void> {
    // Si el envasado rígido editado viene de un estado "Disponible", "Rechazado" o "Bloqueado"
    // se notifica que fue editado
    const selectedEnvasado: Envasado = this.selectedEnvasado!;
    if (
      selectedEnvasado.status.endsWith(appConstants.entity.codes.rejected) ||
      selectedEnvasado.status.endsWith(appConstants.entity.codes.blocked) ||
      selectedEnvasado.status.endsWith(appConstants.entity.codes.available)
    ) {
      let initialStatus: string;
      let messageInitialStatus: string;
      if (
        selectedEnvasado.status.endsWith(appConstants.entity.codes.rejected)
      ) {
        initialStatus = 'FROMREJECTED';
        messageInitialStatus = 'rechazado';
      } else if (
        selectedEnvasado.status.endsWith(appConstants.entity.codes.available)
      ) {
        initialStatus = 'FROMAVAILABLE';
        messageInitialStatus = 'disponible';
      } else {
        initialStatus = 'FROMBLOCKED';
        messageInitialStatus = 'bloqueado';
      }
      const creatNotificationInput: CreateNotificationInput = {
        businessId: selectedEnvasado.business,
        notificationId:
          'ENVASADO#' + selectedEnvasado.envasadoId + '#' + uploadDateTimestamp,
        status: 'TO_BE_SENT',
        model: 'ENVASADO',
        motive: `UPDATE#${initialStatus}#TOTOBEAPPROVED`,
        envasadoId: selectedEnvasado.envasadoId,
      };

      await this.api
        .CreateNotification(creatNotificationInput)
        .then(async () => {
          let message = 'Se ha enviado una notificación por la actualización ';
          message += `del envasado rígido ${this.envasadoIdentification} previamente ${messageInitialStatus}.`;
          this.feedbacksService.showFeedback(message, 'info');
        });
    }
  }

  /**
   * Navega al detalle del envasado rígido.
   */
  onCancel(): void {
    // Si el usuario cancela, se termina el flujo de archivado.
    if (this.archiveMode) {
      this.archivesService.setArchiving(false);
    }
    // Regresamos al detalle del envasado rígido.
    this.router
      .navigate(['../'], { relativeTo: this.route })
      .then(() => console.log('navigate to details'));
  }

  /* --------------------------------------- */
  /* - Métodos para ambiente de desarrollo - */
  /* --------------------------------------- */
  /**
   * Muestra el formulario en la consola.
   * Nota: Solo aparece en DEV.
   */
  showForm(): void {
    console.log('-- Current Form --');
    console.log(this.envasadoForm);
  }

  async ngOnDestroy(): Promise<void> {
    if (this.editionId !== '') {
      await this.editionsService.releaseEdition(this.editionId);
    }
    if (this.archiveMode) {
      this.archivesService.setArchiving(false);
    }
  }
  get mandatorySlider(): boolean {
    return this.envasadoForm.get('onlyMandatory')?.value;
  }
}
